import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { CompanyListItemDto } from "@green0meter/api-client";
import FeatherIcon from "feather-icons-react";
import { Stack } from "@mui/material";

import Table, { TableColumn } from "@components/common/Table";
import { useGetCompanies, useRemoveCompany } from "@api";
import { useUserStore } from "@store/user";
import { Box, IconButton, Tooltip } from "@components/base";
import { ConfirmationModal } from "@components/common";

interface EntitiesTable {
  onRowClick: Table<CompanyListItemDto>["onRowClick"];
}

const EntitiesTable: React.FC<EntitiesTable> = ({ onRowClick }) => {
  const { t } = useTranslation();
  const { user } = useUserStore();
  const { companies, companiesEnabled } = useGetCompanies(user);
  const [removeCompany, setRemoveCompany] = useState<CompanyListItemDto>();
  const { removeCompanyAsync, isPendingRemoveCompany } = useRemoveCompany(user);

  const handleTableRowClick = (entity: CompanyListItemDto): void => {
    onRowClick?.(entity);
  };

  const handleOpenRemoveCompanyDialog = (entity: CompanyListItemDto): void => {
    setRemoveCompany(entity);
  };

  const handleRemoveECompany = async (): Promise<void> => {
    if (removeCompany) {
      await removeCompanyAsync(removeCompany.id);
    }
    setRemoveCompany(undefined);
  };

  const columns: TableColumn<CompanyListItemDto>[] = [
    {
      title: t("entities.table.column.name"),
      key: "name",
      dataKey: "name",
    },
    {
      title: t("entities.table.column.type"),
      key: "companyType",
      renderCellContent: (record): string => {
        return t(`companyType.${record.companyType}`);
      },
    },
    {
      title: t("common.actions"),
      key: "actions",
      renderCellContent: (record): React.ReactNode => {
        return (
          <Stack direction="row" spacing={1} alignItems="flex-end">
            <Tooltip title={t("common.edit")}>
              <IconButton
                onClick={() => handleTableRowClick(record)}
                size="small"
                color="primary"
              >
                <FeatherIcon icon="edit" size={16} />
              </IconButton>
            </Tooltip>
            <Tooltip title={t("common.delete")}>
              <IconButton
                onClick={() => handleOpenRemoveCompanyDialog(record)}
                size="small"
                color="error"
              >
                <FeatherIcon icon="trash-2" size={16} />
              </IconButton>
            </Tooltip>
          </Stack>
        );
      },
      tableCellProps: {
        width: 100,
      },
    },
  ];

  return (
    companiesEnabled && (
      <>
        <Table<CompanyListItemDto>
          data={companies ?? []}
          columns={columns}
          onRowClick={handleTableRowClick}
          tableProps={{
            size: "small",
          }}
          dataPlaceholder={<Box p={2}>{t("entities.table.noData")}</Box>}
        />
        <ConfirmationModal
          open={!!removeCompany}
          onClose={() => setRemoveCompany(undefined)}
          onConfirm={handleRemoveECompany}
          title={t("entities.remove.confirmation.title", {
            company_name: removeCompany?.name,
          })}
          confirmText={t("common.delete")}
          isLoading={isPendingRemoveCompany}
        />
      </>
    )
  );
};

export default EntitiesTable;
